export enum StatusCategory {
  NEW = 'new',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
}

export enum QuestionType {
  YES_NO = 'yes_no',
  MULTIPLE_CHOICE = 'multiple_choice',
  SINGLE_CHOICE = 'single_choice',
  TEXT = 'text',
  NUMERIC = 'numeric',
  DATE = 'date',
}

export enum TransactionType {
  REVENUE = 'revenue',
  EXPENSE = 'expense',
  TRANSFER = 'transfer',
  ADJUSTMENT = 'adjustment',
  COMMITMENT = 'commitment', // For future commitments/obligations
}

export enum TransactionStatus {
  PENDING = 'pending',
  POSTED = 'posted',
  CLEARED = 'cleared',
  VOIDED = 'voided',
}

export enum CapacityUnit {
  CURRENCY = 'currency',
  PERCENTAGE = 'percentage',
  QUANTITY = 'quantity',
}

export enum CapacityStatus {
  MAX = 'max',
  WATCH = 'watch',
  GOOD = 'good',
}

export enum ImprovementStatus {
  QUICK = 'quick',
  MEDIUM = 'medium',
  LONG = 'long',
}

export enum ImprovementState {
  PLANNED = 'planned',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  ON_HOLD = 'on_hold',
}

export enum ValueType {
  ACTUAL = 'actual',
  PROJECTED = 'projected',
  BUDGET = 'budget',
}

export enum DistributionType {
  NORMAL = 'normal',
  LOGNORMAL = 'lognormal',
  TRIANGULAR = 'triangular',
  UNIFORM = 'uniform',
  CUSTOM = 'custom',
}

export enum VariableType {
  GROWTH_RATE = 'growth_rate',
  MARKET_SIZE = 'market_size',
  CONVERSION_RATE = 'conversion_rate',
  SEASONALITY = 'seasonality',
  PRICE = 'price',
  COST = 'cost',
  MARKET_SHARE = 'market_share',
}

export enum DependencyType {
  START_TO_START = 'start_to_start', // B can't start until A starts
  START_TO_FINISH = 'start_to_finish', // B can't finish until A starts
  FINISH_TO_START = 'finish_to_start', // B can't start until A finishes
  FINISH_TO_FINISH = 'finish_to_finish', // B can't finish until A finishes
  STATE_DEPENDENT = 'state_dependent', // B depends on A reaching specific state
}

export enum PeriodType {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  ANNUAL = 'annual',
}

export enum PeriodStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  PROJECTED = 'projected',
}

export enum DependencyStatus {
  BLOCKED = 'blocked', // Prerequisites not met
  ELIGIBLE = 'eligible', // Prerequisites met, can start
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  DEFERRED = 'deferred', // Intentionally delayed
}

export enum CapacityCategory {
  FINANCIAL = 'financial',
  OPERATIONAL = 'operational',
  SALES = 'sales',
  PRODUCTION = 'production',
  RESOURCE = 'resource',
}

export enum TimingType {
  BASELINE = 'baseline',
  ACTUAL = 'actual',
  PREDICTED = 'predicted',
  SIMULATED = 'simulated',
}

export enum ArtifactStateTransitionType {
  OPERATIONAL = 'OPERATIONAL',
  MATERIAL = 'MATERIAL',
  RESOURCE = 'RESOURCE',
  CREATION = 'CREATION',
  IMPROVEMENT = 'IMPROVEMENT',
}

export enum ProcessType {
  DEFAULT = 'DEFAULT',
  DEMAND = 'DEMAND',
  SUPPLY = 'SUPPLY',
  DELIVERY = 'DELIVERY',
  FINANCE = 'FINANCE',
  HR = 'HR',
  IT = 'IT',
  STRATEGY = 'STRATEGY',
  IMPROVEMENT = 'IMPROVEMENT',
  QUALITY = 'QUALITY',
  COMPLIANCE = 'COMPLIANCE',
  RESEARCH = 'RESEARCH',
  MARKETFIT = 'MARKETFIT',
  FITFINDER = 'FITFINDER',
}

/**
 * Defines how demand is set for an artifact state or transition.
 * 
 * CRITICAL for the projection engine to understand how to calculate demand.
 * 
 * @enum {string}
 * @property {string} TARGET - A target value or rate is explicitly entered for this item.
 *                             Used when you know exactly how much you need (e.g., target production rate).
 *                             The projection engine uses this as a fixed demand value.
 * 
 * @property {string} CAPACITY - The resource's capacity determines the demand rate for this item.
 *                               Used when demand is limited by available resource capacity.
 *                               The projection engine calculates demand based on resource constraints.
 * 
 * @property {string} SUPPLY - (DEFAULT) Demand is set by the supply flowing into this item.
 *                             Used in pull-based systems where downstream demand determines upstream flow.
 *                             The projection engine propagates demand backwards through the supply chain.
 */
export enum DemandType {
  TARGET = 'TARGET',
  CAPACITY = 'CAPACITY',
  SUPPLY = 'SUPPLY',
}

/**
 * Defines how demand flows through a transition in the state machine.
 * 
 * CRITICAL for the projection engine to model the flow of demand through processes.
 * 
 * @enum {string}
 * @property {string} DRUM - This item's demand sets the demand flow (system constraint/bottleneck).
 *                          Used to identify the constraint in a Theory of Constraints (TOC) model.
 *                          The projection engine uses this as the primary demand signal that paces the system.
 *                          Only one or few items should be DRUM in a process chain.
 * 
 * @property {string} PULL - (DEFAULT) This item will pull demand as needed from upstream.
 *                          Used in pull-based/lean manufacturing systems.
 *                          The projection engine propagates demand backwards based on actual needs.
 *                          This is the most common flow type for non-constraint items.
 * 
 * @property {string} PUSH - Any demand from this item will push to the next transition.
 *                          Used in push-based/forecast-driven systems.
 *                          The projection engine propagates demand forwards based on forecasts/schedules.
 *                          Typically used for batch processes or make-to-stock scenarios.
 */
export enum DemandFlowType {
  PUSH = 'PUSH',
  PULL = 'PULL',
  DRUM = 'DRUM',
}
